import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Table,
  Space,
  InputNumber,
  Select,
  message,
  Card,
  Divider,
  Empty,
  Row,
  Col,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  ShoppingOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { AModal } from '..';
import api from '../../services/api';
import { formatToBRL } from '../../shared/utils/functions';
import { useI18n } from '../../shared/i18n';

const { Text } = Typography;

interface IProduct {
  id: string;
  name: string;
  code: number;
  unity: string;
  minimum_inventory: number;
  maximum_inventory: number;
}

interface ITreatmentProduct {
  id?: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface ITreatmentProductsModalProps {
  open: boolean;
  onClose: () => void;
  treatmentId: string;
  isDentalTreatment?: boolean;
}

export const TreatmentProductsModal: React.FC<ITreatmentProductsModalProps> = ({
  open,
  onClose,
  treatmentId,
  isDentalTreatment = false,
}) => {
  const { t } = useI18n();
  const [products, setProducts] = useState<IProduct[]>([]);
  const [treatmentProducts, setTreatmentProducts] = useState<
    ITreatmentProduct[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );

  const loadProducts = useCallback(async () => {
    try {
      const response = await api.get('/products/select');
      setProducts(response.data);
    } catch (error) {
      message.error(t('treatmentProductsModal.messages.loadProductsError'));
    }
  }, [t]);

  const loadTreatmentProducts = useCallback(async () => {
    try {
      const endpoint = isDentalTreatment
        ? `/treatments-dental-products/dental-treatment/${treatmentId}`
        : `/treatments-products/treatment/${treatmentId}`;

      const response = await api.get(endpoint);
      setTreatmentProducts(response.data);
    } catch (error) {
      message.error(
        t('treatmentProductsModal.messages.loadTreatmentProductsError'),
      );
    }
  }, [treatmentId, isDentalTreatment, t]);

  useEffect(() => {
    if (open) {
      loadProducts();
      loadTreatmentProducts();
    }
  }, [open, loadProducts, loadTreatmentProducts]);

  const handleAddProduct = useCallback(
    async (productId: string) => {
      if (!productId) return;

      const product = products.find((p) => p.id === productId);
      if (!product) return;

      const newProduct: ITreatmentProduct = {
        product_id: productId,
        quantity: 1,
        unit_price: 0,
        total_price: 0,
      };

      try {
        setLoading(true);
        const endpoint = isDentalTreatment
          ? '/treatments-dental-products'
          : '/treatments-products';

        const data = isDentalTreatment
          ? {
              dental_treatment_id: treatmentId,
              ...newProduct,
            }
          : { treatment_id: treatmentId, ...newProduct };

        const response = await api.post(endpoint, data);
        setTreatmentProducts((prev) => [...prev, response.data]);
        setSelectedProductId(null);
        message.success(t('treatmentProductsModal.messages.addSuccess'));
      } catch (error: any) {
        message.error(
          error.response?.data?.message ||
            t('treatmentProductsModal.messages.addError'),
        );
      } finally {
        setLoading(false);
      }
    },
    [products, treatmentId, isDentalTreatment, t],
  );

  const handleUpdateProduct = useCallback(
    async (id: string, field: string, value: number) => {
      const updatedProducts = treatmentProducts.map((product) => {
        if (product.id === id) {
          const updated = { ...product, [field]: value };
          if (field === 'quantity' || field === 'unit_price') {
            updated.total_price = updated.quantity * updated.unit_price;
          }
          return updated;
        }
        return product;
      });

      setTreatmentProducts(updatedProducts);

      try {
        const endpoint = isDentalTreatment
          ? '/treatments-dental-products'
          : '/treatments-products';
        await api.patch(`${endpoint}/${id}`, {
          [field]: value,
          total_price: updatedProducts.find((p) => p.id === id)?.total_price,
        });
      } catch (error: any) {
        message.error(t('treatmentProductsModal.messages.updateError'));
        loadTreatmentProducts();
      }
    },
    [treatmentProducts, isDentalTreatment, loadTreatmentProducts, t],
  );

  const handleRemoveProduct = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        const endpoint = isDentalTreatment
          ? '/treatments-dental-products'
          : '/treatments-products';
        await api.delete(`${endpoint}/${id}`);
        setTreatmentProducts((prev) => prev.filter((p) => p.id !== id));
        message.success(t('treatmentProductsModal.messages.removeSuccess'));
      } catch (error) {
        message.error(t('treatmentProductsModal.messages.removeError'));
      } finally {
        setLoading(false);
      }
    },
    [isDentalTreatment, t],
  );

  const columns = [
    {
      title: t('treatmentProductsModal.table.product'),
      dataIndex: 'product_id',
      render: (productId: string) => {
        const product = products.find((p) => p.id === productId);
        return product
          ? `${product.code} - ${product.name}`
          : t('treatmentProductsModal.table.notFound');
      },
    },
    {
      title: t('treatmentProductsModal.table.quantity'),
      dataIndex: 'quantity',
      render: (quantity: number, record: ITreatmentProduct) => (
        <InputNumber
          min={1}
          value={quantity}
          onChange={(value) =>
            handleUpdateProduct(record.id || '', 'quantity', value || 1)
          }
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: t('treatmentProductsModal.table.unitPrice'),
      dataIndex: 'unit_price',
      render: (unitPrice: number, record: ITreatmentProduct) => (
        <InputNumber
          min={0}
          step={0.01}
          value={unitPrice}
          onChange={(value) =>
            handleUpdateProduct(record.id || '', 'unit_price', value || 0)
          }
          formatter={formatToBRL}
          style={{ width: 120 }}
        />
      ),
    },
    {
      title: t('treatmentProductsModal.table.total'),
      dataIndex: 'total_price',
      render: (totalPrice: number) => formatToBRL(totalPrice),
    },
    {
      title: t('treatmentProductsModal.table.actions'),
      render: (_: any, record: ITreatmentProduct) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProduct(record.id || '')}
          loading={loading}
        />
      ),
    },
  ];

  const availableProducts = products.filter(
    (product) => !treatmentProducts.some((tp) => tp.product_id === product.id),
  );

  return (
    <AModal
      title={t('treatmentProductsModal.modal.title', {
        suffix: isDentalTreatment
          ? t('treatmentProductsModal.modal.dentalSuffix')
          : '',
      })}
      open={open}
      close={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('treatmentProductsModal.modal.close')}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%', gap: '20px' }}>
        {/* Seção: Adicionar Novo Produto */}
        <Card
          type="inner"
          title={
            <Space>
              <PlusOutlined style={{ color: '#1890ff', fontSize: 16 }} />
              <span>{t('treatmentProductsModal.addSection.title')}</span>
            </Space>
          }
          style={{ borderColor: '#1890ff' }}
        >
          <Row gutter={12} align="middle">
            <Col flex="auto" style={{ minWidth: 250 }}>
              <Text
                type="secondary"
                style={{ display: 'block', marginBottom: 8 }}
              >
                {t('treatmentProductsModal.addSection.helper')}
              </Text>
              <Select
                placeholder={t('treatmentProductsModal.addSection.placeholder')}
                style={{ width: '100%' }}
                value={selectedProductId}
                onChange={setSelectedProductId}
                loading={loading}
                optionLabelProp="label"
              >
                {availableProducts.map((product) => (
                  <Select.Option key={product.id} value={product.id}>
                    <Space>
                      <ShoppingOutlined />
                      <Text strong>{product.code}</Text>
                      <Text>{product.name}</Text>
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleAddProduct(selectedProductId || '')}
                disabled={!selectedProductId}
                loading={loading}
                size="large"
              >
                {t('treatmentProductsModal.addSection.button')}
              </Button>
            </Col>
          </Row>
          {availableProducts.length === 0 && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: '#fafafa',
                borderRadius: 4,
              }}
            >
              <Text type="warning">
                ⚠️ {t('treatmentProductsModal.addSection.allAdded')}
              </Text>
            </div>
          )}
        </Card>

        {/* Divisor */}
        <Divider style={{ margin: '8px 0' }} />

        {/* Seção: Produtos Adicionados */}
        <Card
          type="inner"
          title={
            <Space>
              <EditOutlined style={{ color: '#52c41a', fontSize: 16 }} />
              <span>
                {t('treatmentProductsModal.addedSection.title')}
                {treatmentProducts.length > 0 && (
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, marginLeft: 8 }}
                  >
                    ({treatmentProducts.length})
                  </Text>
                )}
              </span>
            </Space>
          }
        >
          {treatmentProducts.length === 0 ? (
            <Empty
              description={t('treatmentProductsModal.addedSection.empty')}
              style={{ marginTop: 20, marginBottom: 20 }}
            >
              <Text type="secondary">
                {t('treatmentProductsModal.addedSection.emptyHelper')}
              </Text>
            </Empty>
          ) : (
            <>
              <Text
                type="secondary"
                style={{ display: 'block', marginBottom: 16 }}
              >
                {t('treatmentProductsModal.addedSection.helper')}
              </Text>
              <Table
                columns={columns}
                dataSource={treatmentProducts}
                rowKey="id"
                pagination={false}
                size="middle"
                scroll={{ x: true }}
              />
            </>
          )}
        </Card>

        {/* Resumo */}
        {treatmentProducts.length > 0 && (
          <Card
            style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}
            bodyStyle={{ padding: '12px 16px' }}
          >
            <Row justify="space-between">
              <Text strong>
                {t('treatmentProductsModal.summary.totalProducts')}
              </Text>
              <Text strong style={{ fontSize: 16 }}>
                {treatmentProducts.length}
              </Text>
            </Row>
            <Row justify="space-between" style={{ marginTop: 8 }}>
              <Text strong>
                {t('treatmentProductsModal.summary.totalValue')}
              </Text>
              <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                {formatToBRL(
                  treatmentProducts.reduce((sum, p) => sum + p.total_price, 0),
                )}
              </Text>
            </Row>
          </Card>
        )}
      </Space>
    </AModal>
  );
};
